import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import _ from "lodash";
import FilterGroup from "../../common/reports/FilterGroup";
import FilterItem from "../../common/reports/FilterItem";
import Filters from "../../common/reports/Filters";
import { LIKE } from "../../common/reports/reportConstants";
import { createFilterParam } from "../../common/reports/reportUtils";
import Button from "../../framework/Button";
import GlobalContext from "../../framework/GlobalContext";
import Shortcut from "../../framework/Shortcut";
import TextInput from "../../framework/TextInput";
import { executeScroll, isEmpty } from "../../utils/utils";
import NumberInput from "../../framework/NumberInput";

const CacUserFilter = ({
  reportRef,
  onFilterShow,
  applyFilters = () => {},
  applyFilterOnMount = true,
  refreshData = false,
}) => {
  const [userName, setUserName] = useState("");
  const [limit, setLimit] = useState(100);

  const [orderBy] = useState([{ fieldName: "createdTime", orderType: "desc" }]);

  const applyFilterRef = useRef();
  const filterLimitRef = useRef();
  const filterUserNameRef = useRef();
  const { setCurrentComponent } = useContext(GlobalContext);
  const applyFilterHandler = useCallback(
    (isFilterChanged = false) => {
      let filters = [];
      let result = {};
      if (!isEmpty(userName)) {
        filters.push(createFilterParam("username", userName, "string", LIKE));
      }
      if (!isEmpty(limit)) {
        result.limit = limit;
      }
      if (orderBy) {
        result.orderBy = orderBy;
      }
      if (isFilterChanged) {
        result.filters = filters;
      }
      applyFilters(result, {
        userName,
        limit,
        orderBy,
      });
      reportRef.current?.applyFilters(result);
    },
    [userName, limit, orderBy, reportRef, applyFilters]
  );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  let debounce = useCallback(
    _.debounce(() => {
      applyFilterHandler();
    }, 300),
    []
  );
  const filterListener = useCallback((event) => {
    const key = event.key.toLowerCase();
    const ctrl = event.ctrlKey;
    if (ctrl && key === "enter") {
      return applyFilterRef.current.click();
    }
  }, []);

  const filterContent = (
    <>
      <FilterGroup>
        <FilterItem>
          <TextInput
            id="cu_filter_userName"
            label="User Name"
            ref={filterUserNameRef}
            onFocus={() => setCurrentComponent("cu_filter_userName")}
            inputProps={{
              "data-markers": JSON.stringify({
                up: "cu_filter_userName",
                down: "cu_limit_filter",
                right: "cu_limit_filter",
                left: "cu_filter_userName",
              }),
            }}
            value={userName}
            onChange={(e) => {
              setUserName(e.target.value);
            }}
          />
        </FilterItem>
      </FilterGroup>
      <FilterGroup>
        <FilterItem>
          <NumberInput
            id="cu_limit_filter"
            label="Limit"
            ref={filterLimitRef}
            onValueChange={(values) => setLimit(values.floatValue)}
            isAllowed={(values) => values?.formattedValue <= 999999}
            onFocus={() => {
              executeScroll(filterLimitRef);
              setCurrentComponent("cu_limit_filter");
            }}
            value={limit}
            inputProps={{
              "data-markers": JSON.stringify({
                up: "cu_filter_userName",
                down: "cu_apply_filter_button",
                left: "cu_filter_userName",
                right: "cu_apply_filter_button",
              }),
            }}
          />
        </FilterItem>
      </FilterGroup>
    </>
  );
  const filterFooter = (
    <>
      <FilterGroup>
        <FilterItem>
          <Button
            id="cu_apply_filter_button"
            label="Apply"
            ref={applyFilterRef}
            endIcon={<Shortcut keys={["Ctrl", "Enter"]} />}
            onClick={() => {
              applyFilterHandler(true);
            }}
            markers={JSON.stringify({
              up: "cu_limit_filter",
              left: "cu_limit_filter",
              right: "cu_apply_filter_button",
              down: "cu_apply_filter_button",
            })}
          />
        </FilterItem>
      </FilterGroup>
    </>
  );
  useEffect(() => {
    if (refreshData) {
      applyFilterHandler(true);
    }
  }, [refreshData, applyFilterHandler]);
  useEffect(() => {
    if (onFilterShow) {
      filterUserNameRef?.current.focus({ preventScroll: true });
    }
  }, [onFilterShow]);
  useEffect(() => {
    if (onFilterShow) document.addEventListener("keyup", filterListener, false);
    return () => {
      document.removeEventListener("keyup", filterListener, false);
    };
  }, [filterListener, onFilterShow]);
  useEffect(() => {
    if (applyFilterOnMount) debounce();
    return () => {
      debounce.cancel();
    };
  }, [applyFilterOnMount, debounce]);

  return <Filters filterContent={filterContent} filterFooter={filterFooter} />;
};

export default CacUserFilter;
